import { useState } from 'react';
import { Check, FileText } from 'lucide-react';
import { Button } from '@/ui/primitives';
import { useDossie, useDossieTexto, useGerarDossie } from '../../api';
import { DossieMarkdown } from '../DossieMarkdown';

interface Props {
  id: string;
  fallbackStatus?: string | null | undefined;
}

function formatDate(s: string | null | undefined) {
  if (!s) return null;
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
}

// Dossiê consultivo gerado por LLM. Permite regenerar, revisar (com
// instruções livres pra orientar nova geração) e marcar como revisado pra
// avançar a fase.
export function FaseDossie({ id, fallbackStatus }: Props) {
  const dossie = useDossie(id);
  const gerarDossie = useGerarDossie(id);
  const dossieTexto = useDossieTexto(id);
  const [revisaoOpen, setRevisaoOpen] = useState(false);
  const [revisaoTxt, setRevisaoTxt] = useState('');

  function onMarcarDossie(s: 'gerado' | 'revisado' | 'anexado') {
    dossie.mutate({ dossie_status: s });
  }

  return (
    <section>
      <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted mb-2">
        Dossiê consultivo (IA)
      </h3>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={() => gerarDossie.mutate(undefined)}
          disabled={gerarDossie.isPending}
        >
          <FileText size={14} />
          {gerarDossie.isPending
            ? 'Gerando…'
            : dossieTexto.data?.dossie_texto
            ? 'Regenerar'
            : 'Gerar dossiê'}
        </Button>
        {dossieTexto.data?.dossie_texto && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setRevisaoOpen((v) => !v)}
            disabled={gerarDossie.isPending}
          >
            <FileText size={14} />
            {revisaoOpen ? 'Cancelar revisão' : 'Revisar e regenerar'}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onMarcarDossie('revisado')}
          disabled={dossie.isPending || !dossieTexto.data?.dossie_texto}
        >
          <Check size={14} /> Revisado — avançar
        </Button>
      </div>
      <p className="text-[0.78rem] text-text-2 mt-2">
        Status:{' '}
        {String(dossieTexto.data?.dossie_status ?? fallbackStatus ?? 'pendente')}
        {dossieTexto.data?.dossie_modelo ? ` · modelo: ${dossieTexto.data.dossie_modelo}` : ''}
        {dossieTexto.data?.dossie_gerado_em ? ` · ${formatDate(dossieTexto.data.dossie_gerado_em)}` : ''}
      </p>

      {revisaoOpen && (
        <div className="mt-3 p-4 border border-border rounded-lg bg-surface-2 flex flex-col gap-3">
          <div>
            <label
              htmlFor="crm-d-revisao"
              className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
            >
              Instruções para regenerar
            </label>
            <p className="text-[0.78rem] text-text-2 mt-1">
              Diga o que mudar: encurtar, mudar tom, focar em algo, citar uma norma específica, trocar a abordagem comercial, etc.
            </p>
          </div>
          <textarea
            id="crm-d-revisao"
            value={revisaoTxt}
            onChange={(e) => setRevisaoTxt(e.target.value)}
            rows={4}
            placeholder="ex: deixar a abordagem mais curta. Falar direto com o filho que cuida da fazenda. Tirar a parte sobre crédito rural — não é o caso aqui."
            className="w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text
                       placeholder:text-text-muted hover:border-[var(--text-muted)]
                       focus:border-accent focus:outline-none focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15)]
                       resize-none"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (!revisaoTxt.trim()) return;
                gerarDossie.mutate(
                  { revisao: revisaoTxt.trim() },
                  {
                    onSuccess: () => {
                      setRevisaoTxt('');
                      setRevisaoOpen(false);
                    },
                  },
                );
              }}
              disabled={gerarDossie.isPending || !revisaoTxt.trim()}
            >
              {gerarDossie.isPending ? 'Regenerando…' : 'Regenerar com revisão'}
            </Button>
            <span className="text-[0.78rem] text-text-muted">
              A revisão fica registrada no histórico do card.
            </span>
          </div>
        </div>
      )}

      {gerarDossie.error && (
        <p className="text-[0.78rem] text-danger mt-2">
          Falha ao gerar: {(gerarDossie.error as Error).message}
        </p>
      )}
      {dossieTexto.data?.dossie_texto && (
        <div className="mt-4 p-4 bg-surface-2 border border-border rounded-lg">
          <DossieMarkdown texto={dossieTexto.data.dossie_texto} />
        </div>
      )}
    </section>
  );
}
